import { makeAutoObservable, runInAction, action } from 'mobx';
import groupBy from 'lodash/groupBy';
import dayjs, { Dayjs } from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import isObject from 'lodash/isObject';
import { validate } from 'uuid';
import { FieldState } from 'formstate';
import CartDeliveryState from '@features/cart/mobx/state/CartDeliveryState';
import CartProductState from '@features/cart/mobx/state/CartProductState';
import NextCheckoutApi from '@features/api/nextApiService/nextCheckoutApi';
import NextDeliveryApi from '@features/api/nextApiService/nextDeliveryApi';
import logger from '@utils/logger';
import { CartTotalType, CartTotalValueType, ICartAvailableDelivery, ICartItem, TPositionType, AvailabilityType, ICoupon, IDiscountData, IDelivery, ICartService, IAdditionalArgument, ICartData } from '@features/cart/typescript';
import { getRegion } from '@utils/misc';
import { IOrderDetails, IBasicAddress } from '@features/checkout/typescript';
import { IApiError, TRegion } from '@typescript/index';
import { formatPrice } from '@utils/formatPrice';
import ReaffiliatesViewMiniCart from '@reaffiliates/ReaffiliatesViewMiniCart';
import { TOrderStatusType, TOrderTransport, ICart, TCartStatusType } from '@features/userPanel/typescript';
import { ASSEMBLY_SERVICE_NAME, BRINGING_SERVICE_NAME, CLOSET_SUBTYPE_ID, CLOSET_SUBTYPE_MASKING_ID, CLOSET_TYPE_ID, STOR_CATEGORY_ID } from '@features/cart/const';
import { clearSelectedDeliveryAdditionalData } from '@utils/deliveryAdditionalDataHelper';
import browserStorage from '@utils/browserStorage';
import { CANNOT_MODIFY_CART } from '@features/api/platformApiService/const';
import { AVAILABLE_REGIONS } from '@const/index';
import { IDeliveryDataResponse } from '@features/api/platformApiService/typescript';
dayjs.extend(minMax);
export type SideBarType = 'assembly' | 'delivery' | 'instalmentPayment';
export type StorAssemblyState = 'none' | 'pure' | 'mixed';
export default class CartStore {
  public cartId = '';
  public cartStatus: TOrderStatusType | TCartStatusType = '' as TOrderStatusType;
  public customerId = '';
  public deliveryAddressId = '';
  public invoiceAddressId = '';
  public products: Map<string, CartProductState> = new Map();
  public deliveries: Map<string, CartDeliveryState> = new Map();
  public availableDeliveries: Map<string, Array<ICartAvailableDelivery>> = new Map();
  public deliveryDataConfig: Record<string, IDeliveryDataResponse> = {};
  public services: ICartService[] = [];
  public selectedBringingService: Record<string, ICartService> = {};
  public selectedAssemblyService: Record<string, ICartService> = {};
  public selectedDeliveries: Record<string, IDelivery> = {};
  public transports: Array<TOrderTransport> = [] as Array<TOrderTransport>;
  public totals: Record<CartTotalType, CartTotalValueType> = {} as Record<CartTotalType, CartTotalValueType>;
  public isSideBarOpened = false;
  public isFetchingInProgress = false;
  public showProductsAvailableWithDelayMessage = false;
  public showProductsUnavailableMessage = false;
  public showStorAssemblyPureMessage = false;
  public showStorAssemblyMixedMessage = false;
  public showNewTaxRateMessage = false;
  public currentAssemblyTaxRate = '23';
  private confirmedUnavailableQuantityProducts = new Set<string>();
  public discountInputOpened = false;
  public coupon: ICoupon | null = null;
  public discountData: IDiscountData = {} as IDiscountData;
  public discountCode: FieldState<string> = new FieldState('');
  public errorDiscountValidate = false;
  public discountCouponActive = false;
  public spinnerLoader = false;
  public isCartOutdated = false;
  public showChangedDeliveryTimeMessage = false;
  public deliveryDays: number[] | null = null;
  public deliveryAddres: IBasicAddress | null = null;
  public note: string = '';
  public cuponReachedMaxUsage = false;
  constructor() {
    makeAutoObservable(this);
  }
  public loadExternalCart = async (cartId: string) => {
    this.getCartFromLocalStorage();
    const oldCartId = this.cartId;
    this.clearStore();
    this.cartId = cartId;
    const {
      cartId: resultCartId
    } = await this.fetchAndPopulateCartStore();
    if (resultCartId === '') {
      this.cartId = oldCartId;
      await this.fetchAndPopulateCartStore();
    }
    this.saveCartInLocalStorage();
    return resultCartId;
  };
  public fetchAndPopulateCartStore = async (withLoader = true, additionalArgument?: IAdditionalArgument) => {
    runInAction(() => {
      if (withLoader) this.isFetchingInProgress = true;
    });
    const data = await this.handleCartStatus();
    const isOfCartType = (object: ICart | IApiError): object is ICart => 'cartId' in object;
    runInAction(() => {
      this.resetStore();
      if (isOfCartType(data)) this.populateStoreWithFetchedCartData(data);
      if (withLoader) this.isFetchingInProgress = false;
    });
    if (Object.keys(this.products).length > 0) {
      this.fetchAvailableDeliveries(additionalArgument);
    }
    this.checkStorAssembly();
    return {
      cartId: this.cartId,
      products: this.products
    };
  };
  public invalidateCart = async (isCartOrdered = false) => {
    this.cartId = '';
    this.saveCartInLocalStorage();
    clearSelectedDeliveryAdditionalData();
    this.products.clear();
    this.deliveries.clear();
    this.services.length = 0;
    this.transports.length = 0;
    this.totals = {} as Record<CartTotalType, CartTotalValueType>;
    if (!isCartOrdered) {
      NextCheckoutApi.clearOrder();
    }
  };
  private handleCartStatus = async () => {
    let data = {} as ICart | IApiError;
    let isCartCanceled = false;
    let isCartOrdered = false;
    let cartDoesNotExists = false;
    let badCartId = false;
    const isCorrectCartId = this.cartId && this.cartId !== 'undefined' && validate(this.cartId);
    if (isCorrectCartId) {
      data = await NextCheckoutApi.getCart(this.cartId);
      const {
        status
      } = data;
      isCartCanceled = status === 'canceled';
      isCartOrdered = status === 'ordered';
      cartDoesNotExists = status === 404;
      badCartId = status === 422;
    }
    const isCartInvalid = !isCorrectCartId || isCartOrdered || isCartCanceled || cartDoesNotExists || badCartId;
    if (isCartInvalid) {
      this.invalidateCart(isCartOrdered);
      return {} as ICart;
    }
    return data;
  };
  public fetchAvailableDeliveries = async (additionalArgument?: IAdditionalArgument): Promise<boolean> => {
    runInAction(() => {
      this.isFetchingInProgress = true;
    });
    let dataFetchedSuccessfully = false;
    const fetchedDeliveries: Map<string, ICartAvailableDelivery[]> = new Map();
    let additional = additionalArgument;
    const region = getRegion('uppercase') as TRegion;
    if (!additionalArgument && this.deliveryAddres && [AVAILABLE_REGIONS.poland, AVAILABLE_REGIONS.euro].includes(region)) {
      additional = {
        key: 'postalCode',
        value: this.deliveryAddres.postalCode || ''
      };
    }
    if (!additionalArgument && this.deliveryAddres?.country && region === AVAILABLE_REGIONS.euro) {
      additional = {
        key: 'countryCode',
        value: this.deliveryAddres?.country
      };
    }
    await Promise.allSettled(this.transports.map(async transport => {
      const deliveriesResponse = await NextCheckoutApi.getAvailableDeliveries(this.cartId, transport.transportGroupId, additional);
      const {
        success,
        data
      } = deliveriesResponse;
      if (success) {
        dataFetchedSuccessfully = true;
        runInAction(() => {
          if (Array.isArray(data) && data.length) {
            fetchedDeliveries.set(transport.transportGroupId, data);
          }
          const deliveries = fetchedDeliveries.get(transport.transportGroupId);
          if (deliveries) {
            deliveries.forEach(delivery => {
              if (additional?.key === 'postalCode' && additional?.value) {
                delivery.postalCode = additional.value || '';
              } else {
                delivery.postalCode = '';
              }
            });
          }
        });
      } else {
        runInAction(() => {
          const deliveries = fetchedDeliveries.get(transport.transportGroupId);
          if (deliveries) {
            deliveries.forEach(delivery => {
              delivery.price = null;
            });
          }
        });
      }
    }));
    runInAction(() => {
      if (dataFetchedSuccessfully) {
        this.availableDeliveries.clear();
        fetchedDeliveries.forEach((value, key) => {
          this.availableDeliveries.set(key, value);
        });
      }
      this.handleDeliveryTime();
      this.isFetchingInProgress = false;
    });
    return true;
  };
  public createCart = async () => {
    const currentCartId = this.getCartFromLocalStorage();
    if (currentCartId) {
      const data = await this.handleCartStatus();
      const isOfCartType = (object: ICart | IApiError): object is ICart => 'cartId' in object;
      if (isOfCartType(data)) return this.cartId;
    }
    try {
      await NextCheckoutApi.clearOrder();
      clearSelectedDeliveryAdditionalData();
      const cart = await NextCheckoutApi.createCart();
      const {
        cartId
      } = cart;
      runInAction(() => {
        this.cartId = cartId;
        this.saveCartInLocalStorage();
      });
      return this.cartId;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logger.error(error);
      throw new Error(error);
    }
  };
  private resetStore() {
    this.cartId = '';
    this.customerId = '';
    this.deliveryAddressId = '';
    this.invoiceAddressId = '';
    this.products = new Map();
    this.deliveries = new Map();
    this.transports = [];
    this.services = [];
    this.totals = {} as Record<CartTotalType, CartTotalValueType>;
    this.coupon = null;
    this.showProductsAvailableWithDelayMessage = false;
    this.showProductsUnavailableMessage = false;
  }
  private productsFactory(products: Array<ICartItem>) {
    if (!products.length) return;
    products.forEach(product => this.products.set(product.positionId, new CartProductState(this.fetchAndPopulateCartStore, this.cartId, product, this, this.confirmedUnavailableQuantityProducts.has(product.positionId))));
  }
  private servicesFactory(services: Array<ICartService>) {
    if (!services.length) return;
    this.services = services;
  }
  private deliveriesFactory(deliveries: Array<IDelivery>) {
    if (!deliveries.length) return;
    const newDeliveries = new Map<string, CartDeliveryState>();
    deliveries.forEach(delivery => {
      newDeliveries.set(delivery.positionId, new CartDeliveryState(delivery));
    });
    this.deliveries = newDeliveries;
  }
  private checkSelectedDeliveries() {
    const newSelectedDeliveries = new Map<string, IDelivery>();
    this.transports.forEach(transport => {
      const {
        positions,
        transportGroupId
      } = transport;
      positions.forEach(position => {
        const delivery = this.deliveries.get(position);
        if (delivery) {
          newSelectedDeliveries.set(transportGroupId, delivery);
        }
      });
    });
    this.selectedDeliveries = Object.fromEntries(newSelectedDeliveries);
  }
  private checkSelectedServices() {
    const newSelectedBringingService = new Map<string, ICartService>();
    const newSelectedAssemblyService = new Map<string, ICartService>();
    this.transports.forEach(transport => {
      const {
        positions,
        transportGroupId
      } = transport;
      positions.forEach(position => {
        const service = this.services.find(s => s.positionId === position);
        if (service) {
          if (service.name === BRINGING_SERVICE_NAME) {
            newSelectedBringingService.set(transportGroupId, service);
          }
          if (service.name === ASSEMBLY_SERVICE_NAME) {
            newSelectedAssemblyService.set(transportGroupId, service);
          }
        }
      });
    });
    this.selectedBringingService = Object.fromEntries(newSelectedBringingService);
    this.selectedAssemblyService = Object.fromEntries(newSelectedAssemblyService);
  }
  private positionsFactoryDispatcher(type: TPositionType, grouppedByType) {
    if (!('service' in grouppedByType)) {
      this.services = [];
    }
    if (!('delivery' in grouppedByType)) {
      this.deliveries = new Map();
    }
    if (!('product' in grouppedByType)) {
      this.products = new Map();
    }
    switch (type) {
      case 'product':
        this.productsFactory(grouppedByType[type]);
        break;
      case 'delivery':
        this.deliveriesFactory(grouppedByType[type]);
        break;
      case 'service':
        this.servicesFactory(grouppedByType[type]);
        break;
      default:
        break;
    }
  }
  public populateStoreWithFetchedCartData = (cartData: ICart | IOrderDetails) => {
    this.cartStatus = cartData.status;
    this.selectedDeliveries = {};
    this.selectedAssemblyService = {};
    this.selectedBringingService = {};
    Object.keys(cartData).forEach(key => {
      if (!cartData[key]) return;
      if (typeof cartData[key] === 'string') this[key] = cartData[key];
      if (key === 'coupon') {
        this.coupon = isObject(cartData[key]) ? cartData[key] as ICoupon : null;
      }
      if (!cartData[key].length) return;
      if (key === 'positions') {
        const grouppedByType = groupBy(cartData[key], pos => pos.type);
        Object.keys(grouppedByType).forEach(posTypeKey => {
          this.positionsFactoryDispatcher(posTypeKey as TPositionType, grouppedByType);
        });
      }
      if (key === 'totals') {
        cartData[key].forEach(el => {
          this.totals[el.totalType] = el.total;
        });
      }
      if (key === 'transports') {
        cartData[key].forEach(el => {
          this.transports.push(el);
        });
      }
    });
    this.checkSelectedDeliveries();
    this.checkSelectedServices();
    this.checkStorAssembly();
    this.handleDeliveryTime();

    // Fetch delivery data configuration after cart is populated
    this.fetchDeliveryDataConfig();
  };
  public fetchDeliveryDataConfig = async () => {
    if (this.deliveries.size > 0) {
      const deliveryConfigData: Record<string, IDeliveryDataResponse> = {};
      const deliveryPromises = Array.from(this.deliveries.entries()).map(async ([, delivery]) => {
        try {
          const deliveryData = await NextDeliveryApi.getDeliveryData(delivery.deliveryTypeId);
          deliveryConfigData[delivery.deliveryTypeId] = deliveryData;
        } catch (error) {
          logger.error(`Failed to fetch delivery data for ${delivery.deliveryTypeId}:`, error);
          deliveryConfigData[delivery.deliveryTypeId] = {
            error: true,
            message: 'Failed to fetch delivery data',
            deliveryTypeNameLanguageKey: '',
            deliveryType: '',
            isBringingAvailable: false,
            isAssembleAvailable: false,
            isVatAvailable: false,
            isTaxAvailable: false,
            isPaidOnDelivery: false,
            personalPickupStoreType: null,
            leadTime: 0,
            logisticsTime: 0,
            provider: '',
            availabilityStatus: '',
            availabilityDate: '',
            deliveryDate: '',
            pricingWithRequiredServices: [],
            configuration: {
              additionalFields: {
                comment: {
                  exist: false,
                  required: false
                },
                customPrice: {
                  exist: false,
                  required: false
                },
                parcelLocker: {
                  exist: false,
                  required: false
                },
                pickupPointId: {
                  exist: false,
                  required: false
                }
              },
              requirements: {
                retailCustomer: false,
                assemblyService: false,
                bringService: false
              }
            }
          };
        }
      });
      await Promise.all(deliveryPromises);
      runInAction(() => {
        this.deliveryDataConfig = deliveryConfigData;
      });
    }
  };
  public getLatestDeliveryForPosition = (positionId: string): number => {
    let businessDaysLeadTime = 10;
    this.transports.forEach(transport => {
      const transportHasPosition = transport.positions.includes(positionId);
      if (transportHasPosition) {
        const {
          transportGroupId
        } = transport;
        const deliveries = this.availableDeliveries.get(transportGroupId);
        if (deliveries) {
          const transportBusinessDaysLeadTime = Math.min(...deliveries.map((d: ICartAvailableDelivery) => d.logisticsTime + d.leadTime));
          if (transportBusinessDaysLeadTime) businessDaysLeadTime = transportBusinessDaysLeadTime;
        }
      }
    });
    return businessDaysLeadTime;
  };
  public getLatestAvailabilityDatePerTransportGroup = (): Record<string, Dayjs> => {
    const availabilityDatesRecord: Record<string, Dayjs> = {};
    this.transports.forEach(transport => {
      const {
        transportGroupId,
        positions
      } = transport;
      const products = Array.from(this.products.values()).filter(product => positions.includes(product.position));
      const availabilityDates: Dayjs[] = [];
      products.forEach(product => {
        const {
          status,
          dateFrom
        } = product.availability;
        const isAvailable = status === AvailabilityType.available;
        const parsedDate = dateFrom.replace('UTC', '');
        if (isAvailable && dayjs(parsedDate).isValid()) {
          const date = dayjs(parsedDate).isValid() ? dayjs(parsedDate) : dayjs(new Date());
          const onlyFutureDate = dayjs.max(date, dayjs(new Date()));
          availabilityDates.push(onlyFutureDate);
        }
      });
      if (availabilityDates.length === 0) {
        const product = products[0];
        const {
          dateFrom
        } = product.availability;
        const parsedDate = dateFrom.replace('UTC', '');
        if (dayjs(parsedDate).isValid()) {
          const date = dayjs(parsedDate).isValid() ? dayjs(parsedDate) : dayjs(new Date());
          const onlyFutureDate = dayjs.max(date, dayjs(new Date()));
          availabilityDates.push(onlyFutureDate);
        }
      }
      availabilityDatesRecord[transportGroupId] = dayjs.max(availabilityDates);
    });
    return availabilityDatesRecord;
  };
  public get estimatedDelivery(): Record<string, {
    days: number;
    date: string;
  }> {
    if (!this.availableDeliveries.size) return {};
    const estimatedDelivery: Record<string, {
      days: number;
      date: string;
    }> = {};
    const currentDayTime = dayjs(new Date().setHours(0, 0, 0, 0));
    this.transports.forEach(transport => {
      const {
        transportGroupId
      } = transport;
      const selectedDelivery = this.availableDeliveries.get(transportGroupId)?.find(d => d.deliveryTypeId === this.selectedDeliveries[transportGroupId]?.deliveryTypeId);
      if (selectedDelivery) {
        const {
          expectedDeliveryDate
        } = selectedDelivery;

        // W przypadku wybranej dostawy leadTime już zawiera dni robocze
        const deliveryDate = dayjs(expectedDeliveryDate);
        const daysFromNowToDelivery = dayjs(deliveryDate).diff(currentDayTime, 'd');
        estimatedDelivery[transportGroupId] = {
          days: daysFromNowToDelivery,
          date: deliveryDate.format('DD.MM.YYYY')
        };
      } else {
        const deliveries = this.availableDeliveries.get(transportGroupId);
        if (deliveries) {
          const earliestDeliveryDate = deliveries.map((d: ICartAvailableDelivery) => dayjs(d.expectedDeliveryDate)).reduce((earliest, current) => current.isBefore(earliest) ? current : earliest);
          const daysFromNowToDelivery = dayjs(earliestDeliveryDate).diff(currentDayTime, 'd');
          estimatedDelivery[transportGroupId] = {
            days: daysFromNowToDelivery,
            date: earliestDeliveryDate.format('DD.MM.YYYY')
          };
        }
      }
    });
    if (Object.keys(estimatedDelivery).length <= 0) return {};
    return estimatedDelivery;
  }
  public get totalQuantity() {
    const quantities: Array<number> = [0];
    this.products.forEach(product => {
      quantities.push(product.quantity);
    });
    return quantities.reduce((a, b) => a + b);
  }
  public openMiniSideBarCart = () => {
    this.isSideBarOpened = this.totalQuantity > 0;
    if (this.isSideBarOpened) {
      ReaffiliatesViewMiniCart();
    }
  };
  public closeMiniSideBarCart = () => {
    this.isSideBarOpened = false;
  };
  public closeStorAssemblyPureMessage = () => {
    this.showStorAssemblyPureMessage = false;
  };
  public closeStorAssemblyMixedMessage = () => {
    this.showStorAssemblyMixedMessage = false;
  };
  public closeNewTaxRateMessage = () => {
    this.showNewTaxRateMessage = false;
  };
  public get isLoading() {
    const loadingStatuses: Array<boolean> = [this.isFetchingInProgress];
    this.products.forEach(product => {
      loadingStatuses.push(product.isProductLoading);
    });
    return loadingStatuses.some(status => status);
  }
  private saveCartInLocalStorage = () => {
    if (typeof window === 'undefined') return;
    const region = getRegion();
    if (browserStorage) {
      browserStorage.setItem(`cartId_${region}`, this.cartId);
    }
  };
  public getCartFromLocalStorage = (): boolean => {
    if (typeof window === 'undefined') return false;
    const region = getRegion();
    const cartId = browserStorage ? browserStorage.getItem(`cartId_${region}`) : null;
    if (cartId) {
      runInAction(() => {
        this.cartId = cartId;
      });
    }
    return !!cartId;
  };
  public get unavailableProductsQuantityToConfirm() {
    const unavailableProducts = Array.from(this.products.values()).filter((product: CartProductState) => !product.showProductQuantityLimitedMessage && this.confirmedUnavailableQuantityProducts.has(product.position));
    return unavailableProducts.filter((value, index, currentValue) => currentValue.indexOf(value) === index);
  }
  public get unavailableProductsQuantity() {
    const unavailableProducts = Array.from(this.products.values()).filter((product: CartProductState) => product.showProductQuantityLimitedMessage || product.showProductUnavailableMessage);
    if (unavailableProducts.length > 0) {
      runInAction(() => {
        this.showProductsUnavailableMessage = true;
      });
    }
    return unavailableProducts;
  }
  public confirmUnavailableQuantity = action((position: string | null, deleteProduct = false) => {
    if (position && !deleteProduct) {
      this.confirmedUnavailableQuantityProducts.add(position);
    } else if (position && deleteProduct) {
      this.confirmedUnavailableQuantityProducts.delete(position);
    }
  });
  public get productsOutOfStock() {
    const productsTemporaryUnavailable = Array.from(this.products.values()).filter((product: CartProductState) => product.availability.status === AvailabilityType.available && product.availability.amount === 0);
    if (productsTemporaryUnavailable.length > 0) {
      runInAction(() => {
        this.showProductsAvailableWithDelayMessage = true;
      });
    }
    return productsTemporaryUnavailable;
  }
  public closeProductsAvailableWithDelayMessage = () => {
    this.showProductsAvailableWithDelayMessage = false;
  };
  public closeProductsUnavailableMessage = () => {
    this.showProductsUnavailableMessage = false;
  };
  public setProperty = (key: string, value: string | boolean) => {
    this[key] = value;
  };
  public clearStore = () => {
    this.resetStore();
    this.saveCartInLocalStorage();
  };
  public setDiscountInputOpened = (value: boolean) => {
    this.discountInputOpened = value;
  };
  public getDiscountValue = (): string | false => {
    if (!this.coupon || this.totals.discounts.amount === 0) return false;
    return formatPrice(this.totals.discounts.amount);
  };
  public setErrorDiscountValidate = (value: boolean): void => {
    this.errorDiscountValidate = value;
  };
  public setDiscountCouponActive = (value: boolean): void => {
    this.discountCouponActive = value;
  };
  private setDiscountData = (data, discountCode): void => {
    this.discountData = {
      discountCode,
      validationStatus: data.success,
      message: data.message,
      discountValue: data.success && this.getDiscountValue(),
      coupon: this.coupon
    };
  };
  public processDiscountCode = async (discountCode: string | false = false): Promise<void> => {
    let data: JSON = {} as JSON;
    this.spinnerLoader = true;
    this.cuponReachedMaxUsage = false;
    try {
      data = discountCode ? await NextCheckoutApi.checkDiscountCode(this.cartId, discountCode) : await NextCheckoutApi.removeDiscountCode(this.cartId);
      runInAction(() => {
        this.fetchAndPopulateCartStore(false);
        this.setDiscountData(data, discountCode);
        this.spinnerLoader = false;
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      logger.error(e);
      throw new Error(e);
    }
  };
  public setIsCartOutdated = (value: boolean): void => {
    this.isCartOutdated = value;
  };
  public setCuponReachedMaxUsage = (value: boolean): void => {
    this.cuponReachedMaxUsage = value;
  };
  public filterOnlyAssembableProducts = (productsToFilter: CartProductState[]) => productsToFilter.filter(product => product.isAssemblable);
  private handleDeliveryTime = () => {
    const newDeliveryDays: number[] = [];
    const estimated = this.estimatedDelivery;
    if (Object.values(estimated).length) {
      Object.values(estimated).forEach(d => newDeliveryDays.push(d.days));
      if (newDeliveryDays?.length && this.deliveryDays !== null) {
        newDeliveryDays.forEach((day, index) => {
          if (this.deliveryDays && !this.deliveryDays[index] || this.deliveryDays && this.deliveryDays[index] !== day) {
            runInAction(() => {
              this.showChangedDeliveryTimeMessage = true;
            });
          }
        });
      }
    }
    if (newDeliveryDays.length) {
      this.deliveryDays = newDeliveryDays;
    } else {
      this.deliveryDays = null;
      runInAction(() => {
        this.showChangedDeliveryTimeMessage = false;
      });
    }
  };
  public closeChangedDeliveryTimeMessage = () => {
    this.showChangedDeliveryTimeMessage = false;
  };
  public get minPriceDelivery(): number {
    let pricesArray: Record<string, number[]> = {};
    this.transports.forEach(transport => {
      pricesArray = {
        ...pricesArray,
        [transport.transportGroupId]: []
      };
    });
    if (!this.availableDeliveries.size) {
      let price = 0;
      this.transports.forEach(transport => {
        price += transport.totalPrice?.amount || 0;
      });
      return price;
    }
    this.transports.forEach(transport => {
      this.availableDeliveries.get(transport.transportGroupId)?.forEach(delivery => {
        if (typeof delivery.price?.amount === 'number') {
          pricesArray[transport.transportGroupId].push(delivery.price.amount);
        }
      });
    });
    let minPrice = 0;
    this.transports.forEach(transport => {
      minPrice += Math.min(...pricesArray[transport.transportGroupId]);
    });
    return minPrice;
  }
  public refreshCart = async () => {
    const data = await this.handleCartStatus();
    const isOfCartType = (object: ICart | IApiError): object is ICart => 'cartId' in object;
    runInAction(() => {
      if (isOfCartType(data)) {
        const cartData = data;
        this.cartStatus = cartData.status;
        Object.keys(cartData).forEach(key => {
          if (!cartData[key]) return;
          if (typeof cartData[key] === 'string') this[key] = cartData[key];
          if (key === 'coupon') {
            this.coupon = isObject(cartData[key]) ? cartData[key] as ICoupon : null;
          }
          if (!cartData[key].length) return;
          if (key === 'positions') {
            const grouppedByType = groupBy(cartData[key], pos => pos.type);
            Object.keys(grouppedByType).forEach(posTypeKey => {
              this.positionsFactoryDispatcher(posTypeKey as TPositionType, grouppedByType);
            });
          }
          if (key === 'totals') {
            cartData[key].forEach(el => {
              this.totals[el.totalType] = el.total;
            });
          }
          if (key === 'transports') {
            const newTransports = cartData[key].map(el => ({
              ...el,
              totalPrice: el.totalPrice
            }));
            this.transports = newTransports;
          }
        });
        this.checkSelectedDeliveries();
        this.checkSelectedServices();
        this.checkStorAssembly();
      }
    });
  };
  public addProductsToCart = async (productArray: ICartData[]) => {
    if (!this.cartId) {
      await this.createCart();
    }
    let addPositionsResponse = await NextCheckoutApi.addPositions(this.cartId, productArray);
    if ('errorsData' in addPositionsResponse) {
      const {
        errorsData
      } = addPositionsResponse;
      const errorObject = Array.isArray(errorsData) ? errorsData[0] : {};
      if ('error' in errorObject && 'message' in errorObject && 'status' in errorObject && errorObject.status === 409 && errorObject.message === CANNOT_MODIFY_CART) {
        await this.invalidateCart();
        await this.createCart();
        addPositionsResponse = await NextCheckoutApi.addPositions(this.cartId, productArray);
      }
    }
    return addPositionsResponse;
  };
  get totalProductsTotalCurrentGrossAmount() {
    return Array.from(this.products.values()).reduce((acc, product) => acc + product.prices.total.current.gross.amount, 0);
  }
  get totalProductsTotalCurrentAssemblyAmount() {
    return Array.from(this.products.values()).reduce((acc, product) => acc + product.prices.total.current.assembly.amount, 0);
  }
  public checkStorAssembly = () => {
    const region = getRegion('uppercase') as TRegion;
    if (region !== AVAILABLE_REGIONS.poland || Object.keys(this.products).length === 0) {
      return;
    }
    const productsArray = Array.from(this.products.values());
    this.showStorAssemblyPureMessage = false;
    this.showStorAssemblyMixedMessage = false;
    if (productsArray.length === 0) return;
    let hasStoreProducts = false;
    let hasSecondTypeProducts = false;
    let hasOtherProducts = false;

    // Single pass through products array
    hasStoreProducts = productsArray.some(product => product.productType?.id === CLOSET_TYPE_ID && product.productSubtype?.id === CLOSET_SUBTYPE_ID && product.categories.some(category => category.categoryId === STOR_CATEGORY_ID));
    hasSecondTypeProducts = productsArray.some(product => product.productType?.id === CLOSET_TYPE_ID && product.productSubtype?.id === CLOSET_SUBTYPE_MASKING_ID);
    hasOtherProducts = productsArray.some(product => !product.categories.some(category => category.categoryId === STOR_CATEGORY_ID));

    // Determine the state based on what we found
    const hasRequiredProducts = hasStoreProducts && hasSecondTypeProducts;
    if (!hasRequiredProducts) {
      return;
    }
    if (hasRequiredProducts && !hasOtherProducts) {
      runInAction(() => {
        this.showStorAssemblyPureMessage = true;
      });
    } else {
      runInAction(() => {
        this.showStorAssemblyMixedMessage = true;
      });
    }

    // check if any of selected assembly has tax rate 8
    this.showNewTaxRateMessage = false;
    let hasNewTaxRate = false;
    this.transports.forEach(transport => {
      if (Object.keys(this.selectedAssemblyService).length > 0 && this.selectedAssemblyService[transport.transportGroupId] && this.selectedAssemblyService[transport.transportGroupId]?.prices.taxRate.toString() === '8') {
        hasNewTaxRate = true;
      }
    });
    if (hasNewTaxRate && this.currentAssemblyTaxRate !== '8' || !hasNewTaxRate && this.currentAssemblyTaxRate === '8') {
      this.showNewTaxRateMessage = true;
      this.currentAssemblyTaxRate = hasNewTaxRate ? '8' : '23';
    }
  };
  get isAssembly8VatSelected(): boolean {
    let isAssembly8VatSelected = false;
    this.transports.forEach(transport => {
      if (Object.keys(this.selectedAssemblyService).length > 0 && this.selectedAssemblyService[transport.transportGroupId]?.prices.taxRate === '8') {
        isAssembly8VatSelected = true;
      }
    });
    return isAssembly8VatSelected;
  }
  public setCustomerId = (customerId: string) => {
    this.customerId = customerId;
  };
}